import React, { useState, useEffect } from "react";
import { Switch, Route, Redirect, useLocation, Link } from "react-router-dom";

// components
import AdminNavbar from "components/Navbars/AdminNavbar";
import Sidebar from "components/Sidebar/Sidebar";
import FooterAdmin from "components/Footers/FooterAdmin.js";

// views
import Dashboard from "views/admin/Dashboard";
import Members from "views/admin/members/Members";

// IMPORT DE VOS NOUVELLES PAGES
import MemberRegister from "views/admin/members/MemberRegister";
import MemberDetail from "views/admin/members/MemberDetail";
import Roles from "views/admin/roles/Roles";
import UserList from "views/admin/users/UserList";
import UserRegister from "views/admin/users/UserRegister";
import UserStatusToggle from "views/admin/users/UserStatusToggle";
import ProfileEdit from "views/admin/users/ProfileEdit";
import UserService from "services/user.service";
import { AuthService } from "services/auth.service";
import DepartmentList from "views/admin/departments/DepartmentList";
import DepartmentRegister from "views/admin/departments/DepartmentRegister";
import RoleForm from "views/admin/roles/RoleForm";
import ContributionTypeList from "views/admin/contribution-types/ContributionTypeList";
import ContributionTypeRegister from "views/admin/contribution-types/ContributionTypeRegister";
import EventList from "views/admin/events/EventList";
import EventRegister from "views/admin/events/EventRegister";
import AttendanceList from "views/admin/attendances/AttendanceList";
import AttendanceRegister from "views/admin/attendances/AttendanceRegister";
import ContributionList from "views/admin/contributions/ContributionList";
import ContributionRegister from "views/admin/contributions/ContributionRegister";
import OfferingList from "views/admin/offerings/OfferingList";
import OfferingRegister from "views/admin/offerings/OfferingRegister";
import ExpenseList from "views/admin/expenses/ExpenseList";
import ExpenseRegister from "views/admin/expenses/ExpenseRegister";
import PermissionList from "views/admin/permissions/PermissionList";
import PermissionRegister from "views/admin/permissions/PermissionRegister";
import EventTypeList from "views/admin/event-types/EventTypeList";
import EventTypeRegister from "views/admin/event-types/EventTypeRegister";
import TitheList from "views/admin/tithes/TitheList";
import TitheRegister from "views/admin/tithes/TitheRegister";
import EcodimeClassList from "views/admin/ecodime-classes/EcodimeClassList";
import EcodimeClassRegister from "views/admin/ecodime-classes/EcodimeClassRegister";
import TrainingList from "views/admin/trainings/TrainingList";
import TrainingRegister from "views/admin/trainings/TrainingRegister";
import MemberTrainingList from "views/admin/member-trainings/MemberTrainingList";
import MemberTrainingRegister from "views/admin/member-trainings/MemberTrainingRegister";
import AuditLogList from "views/admin/audit-logs/AuditLogList";
import FundList from "views/admin/funds/FundList";
import FundRegister from "views/admin/funds/FundRegister";
import ResourcePermissionList from "views/admin/resource-permissions/ResourcePermissionList";
import ResourcePermissionRegister from "views/admin/resource-permissions/ResourcePermissionRegister";

// Dictionnaire de traduction des segments de l'URL pour le fil d'Ariane
const BREADCRUMB_MAP: Record<string, string> = {
  admin: "Accueil",
  dashboard: "Tableau de Bord",
  departments: "Départements",
  "contribution-types": "Types de Contribution",
  contributions: "Contributions",
  events: "Événements",
  offerings: "Offrandes",
  expenses: "Dépenses",
  attendances: "Présences",
  members: "Membres",
  roles: "Rôles",
  users: "Utilisateurs",
  "event-types": "Types d'Événement",
  permissions: "Permissions",
  funds: "Caisse",
  tithes: "Dîmes",
  "ecodime-classes": "Classes Écodime",
  trainings: "Formations",
  "member-trainings": "Suivi Formations",
  "resource-permissions": "Droits d'accès aux ressources",
  "audit-logs": "Pistes d'Audit",
  profile: "Mon Profil",
  register: "Créer",
  edit: "Modifier",
  view: "Détails",
  status: "Statut",
};

export default function Admin() {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);

  const isDashboard = location.pathname === "/admin/dashboard";

  const fetchUserData = () => {
    UserService.getCurrentAccount()
      .then((res) => setCurrentUser(res.data))
      .catch(() => AuthService.logout());
  };

  useEffect(() => {
    fetchUserData();

    const handleUpdate = () => {
      fetchUserData();
    };

    window.addEventListener("userProfileUpdated", handleUpdate);

    return () => {
      window.removeEventListener("userProfileUpdated", handleUpdate);
    };
  }, []);

  // Fonction de génération dynamique du Breadcrumb
  const renderBreadcrumb = () => {
    const pathnames = location.pathname.split("/").filter((x) => x);

    return (
      <nav className="flex text-slate-400 text-xs font-bold uppercase tracking-wider items-center gap-2">
        <Link
          to="/admin/dashboard"
          className="hover:text-white transition-colors flex items-center"
        >
          <i className="fas fa-home text-sm mr-1.5 text-[#D4AF37]"></i>
          Admin
        </Link>

        {pathnames.map((value, index) => {
          // Ignorer le premier segment 'admin' puisqu'on a déjà mis le bouton d'accueil
          if (value === "admin") return null;

          // Si le segment est un ID numérique ou un UUID, on affiche un label générique ou l'ID abrégé
          const isId = !isNaN(Number(value)) || value.length > 15;
          const label = isId
            ? `#${value.substring(0, 6)}`
            : BREADCRUMB_MAP[value] || value;

          const to = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;

          return (
            <React.Fragment key={to}>
              <span className="text-slate-500 font-normal text-sm mx-1">/</span>
              {isLast ? (
                <span className="text-white font-black text-[#D4AF37]">
                  {label}
                </span>
              ) : (
                <Link to={to} className="hover:text-white transition-colors">
                  {label}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    );
  };

  return (
    <>
      <Sidebar currentUser={currentUser} />
      <div className="relative md:ml-64 bg-slate-100 min-h-screen">
        <AdminNavbar />

        {/* LOGIQUE DE HEADER HARMONISÉE */}
        {isDashboard ? (
          <div className="relative md:pt-32 pb-12 pt-6"></div>
        ) : (
          /* Bandeau Slate constant contenant le Fil d'Ariane */
          <div className="relative bg-[#1e293b] md:pt-24 pb-4 pt-10 px-4 md:px-10">
            <div className="mx-auto w-full flex items-center justify-between">
              {renderBreadcrumb()}
            </div>
          </div>
        )}

        <div className="px-4 md:px-10 mx-auto w-full -m-24">
          <Switch>
            <Route path="/admin/dashboard" exact component={Dashboard} />

            {/* ROUTES DEPARTEMENT */}
            <Route path="/admin/departments" exact component={DepartmentList} />
            <Route
              path="/admin/departments/register"
              exact
              component={DepartmentRegister}
            />
            <Route
              path="/admin/departments/edit/:id"
              exact
              component={DepartmentRegister}
            />

            {/* ROUTES TYPE DE CONTRIBUTION */}
            <Route
              path="/admin/contribution-types"
              exact
              component={ContributionTypeList}
            />
            <Route
              path="/admin/contribution-types/register"
              exact
              component={ContributionTypeRegister}
            />
            <Route
              path="/admin/contribution-types/edit/:id"
              exact
              component={ContributionTypeRegister}
            />

            {/* ROUTES CONTRIBUTION */}
            <Route
              path="/admin/contributions"
              exact
              component={ContributionList}
            />
            <Route
              path="/admin/contributions/register"
              exact
              component={ContributionRegister}
            />
            <Route
              path="/admin/contributions/edit/:id"
              exact
              component={ContributionRegister}
            />

            {/* ROUTES POUR EVENEMENT */}
            <Route path="/admin/events" exact component={EventList} />
            <Route
              path="/admin/events/register"
              exact
              component={EventRegister}
            />
            <Route
              path="/admin/events/edit/:id"
              exact
              component={EventRegister}
            />

            {/* ROUTES POUR OFFRANDE */}
            <Route path="/admin/offerings" exact component={OfferingList} />
            <Route
              path="/admin/offerings/register"
              exact
              component={OfferingRegister}
            />
            <Route
              path="/admin/offerings/edit/:id"
              exact
              component={OfferingRegister}
            />

            {/* ROUTES POUR EXPENSE */}
            <Route path="/admin/expenses" exact component={ExpenseList} />
            <Route
              path="/admin/expenses/register"
              exact
              component={ExpenseRegister}
            />
            <Route
              path="/admin/expenses/edit/:id"
              exact
              component={ExpenseRegister}
            />

            {/* ROUTES POUR PRESENCE */}
            <Route path="/admin/attendances" exact component={AttendanceList} />
            <Route
              path="/admin/attendances/register"
              exact
              component={AttendanceRegister}
            />
            <Route
              path="/admin/attendances/edit/:id"
              exact
              component={AttendanceRegister}
            />

            {/* ROUTES MEMBRES */}
            <Route path="/admin/members" exact component={Members} />
            <Route
              path="/admin/members/register"
              exact
              component={MemberRegister}
            />
            <Route
              path="/admin/members/edit/:id"
              exact
              component={MemberRegister}
            />
            <Route
              path="/admin/members/view/:id"
              exact
              component={MemberDetail}
            />

            {/* ROUTES ROLE */}
            <Route path="/admin/roles" exact component={Roles} />
            <Route path="/admin/roles/register" exact component={RoleForm} />
            <Route path="/admin/roles/edit/:name" exact component={RoleForm} />

            {/* ROUTES USER */}
            <Route path="/admin/users" exact component={UserList} />
            <Route
              path="/admin/users/register"
              exact
              component={UserRegister}
            />
            <Route
              path="/admin/users/edit/:id"
              exact
              component={UserRegister}
            />
            <Route
              path="/admin/users/status/:id"
              exact
              component={UserStatusToggle}
            />

            {/* ROUTES TYPE DE CONTRIBUTION */}
            <Route path="/admin/event-types" exact component={EventTypeList} />
            <Route
              path="/admin/event-types/register"
              exact
              component={EventTypeRegister}
            />
            <Route
              path="/admin/event-types/edit/:id"
              exact
              component={EventTypeRegister}
            />

            {/* ROUTES PERMISSION */}
            <Route path="/admin/permissions" exact component={PermissionList} />
            <Route
              path="/admin/permissions/register"
              exact
              component={PermissionRegister}
            />

            {/* ROUTES tithe */}
            <Route path="/admin/tithes" exact component={TitheList} />
            <Route
              path="/admin/tithes/register"
              exact
              component={TitheRegister}
            />
            <Route
              path="/admin/tithes/edit/:id"
              exact
              component={TitheRegister}
            />

            {/* ROUTES fund */}
            <Route path="/admin/funds" exact component={FundList} />
            <Route
              path="/admin/funds/register"
              exact
              component={FundRegister}
            />
            <Route
              path="/admin/funds/edit/:id"
              exact
              component={FundRegister}
            />

            {/* ROUTES Ressource-permission */}
            <Route
              path="/admin/resource-permissions"
              exact
              component={ResourcePermissionList}
            />
            <Route
              path="/admin/resource-permissions/register"
              exact
              component={ResourcePermissionRegister}
            />
            <Route
              path="/admin/resource-permissions/edit/:id"
              exact
              component={ResourcePermissionRegister}
            />

            {/* ROUTES ecodime-classes */}
            <Route
              path="/admin/ecodime-classes"
              exact
              component={EcodimeClassList}
            />
            <Route
              path="/admin/ecodime-classes/register"
              exact
              component={EcodimeClassRegister}
            />
            <Route
              path="/admin/ecodime-classes/edit/:id"
              exact
              component={EcodimeClassRegister}
            />

            {/* ROUTES trainings */}
            <Route path="/admin/trainings" exact component={TrainingList} />
            <Route
              path="/admin/trainings/register"
              exact
              component={TrainingRegister}
            />
            <Route
              path="/admin/trainings/edit/:id"
              exact
              component={TrainingRegister}
            />

            {/* ROUTES member-trainings */}
            <Route
              path="/admin/member-trainings"
              exact
              component={MemberTrainingList}
            />
            <Route
              path="/admin/member-trainings/register"
              exact
              component={MemberTrainingRegister}
            />
            <Route
              path="/admin/member-trainings/edit/:id"
              exact
              component={MemberTrainingRegister}
            />

            {/* ROUTES Audit */}
            <Route path="/admin/audit-logs" exact component={AuditLogList} />

            <Route path="/admin/profile" exact component={ProfileEdit} />

            <Redirect from="/admin" to="/admin/dashboard" />
          </Switch>
          <FooterAdmin />
        </div>
      </div>
    </>
  );
}
